import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Card } from '@/components/ui'
import { Hint } from '@/components/Hint'
import { kpiDelta, type Better } from '@/lib/analyticsView'
import { cn, formatHours, formatMoney, formatNumber, formatPercent } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'

/**
 * A dashboard number that opens the list behind it.
 *
 * `StatTile` is the read-only version and stays as it is for the screens that
 * use it. This one differs in the two ways the dashboards needed: the whole
 * tile is a link — "22 van roka" lands on those 22 — and the comparison says
 * what it is compared *with*, in the reader's language, and in the unit of
 * the figure rather than always as a percentage.
 *
 * ## Why the question mark is beside the link, not in it
 *
 * A button inside a link is two targets in one, and a tap on the question mark
 * would also follow the link. So the tile is a grid of one cell holding the
 * link and, laid over its corner, the hint — the arrangement `ExplainedTile`
 * on the old home screen used for the same reason.
 *
 * ## Direction is never colour alone
 *
 * An arrow, a sign and a sentence for screen readers carry it; the colour only
 * says whether the move is good news, which depends on the tile — fewer late
 * reports is, fewer resolved is not.
 */

export type KpiUnit = 'count' | 'points' | 'hours' | 'money'

export interface KpiComparison {
  previous: number | null | undefined
  better: Better
  /**
   * `period` for a flow — reports filed, hours to resolve — compared with the
   * previous window; `start` for a stock — what is open now — compared with
   * how it stood when this window began.
   */
  against: 'period' | 'start'
}

export function KpiTile({
  label,
  value,
  to,
  current,
  unit = 'count',
  comparison,
  tone = 'neutral',
  hint,
  explain,
  linkLabel,
}: {
  label: string
  /** The figure as printed. */
  value: string
  /** The list behind it. */
  to: string
  /** The figure as a number, for the comparison. */
  current?: number | null
  unit?: KpiUnit
  comparison?: KpiComparison
  tone?: 'neutral' | 'good' | 'warn' | 'bad'
  /** One short line under the figure. */
  hint?: ReactNode
  /** The sentence behind the question mark. */
  explain?: string
  /** What the link opens, for a screen reader: "Prikaži prijave van roka". */
  linkLabel?: string
}) {
  const toneClass = {
    neutral: '',
    good: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
    bad: 'text-rose-600 dark:text-rose-400',
  }[tone]

  return (
    <div className={cn('relative grid', explain && '[&_[data-kpi-label]]:pr-6')}>
      <Link
        to={to}
        aria-label={linkLabel ? `${label}: ${value}. ${linkLabel}` : undefined}
        className="group focus-visible:ring-ring/60 rounded-2xl outline-none focus-visible:ring-[3px]"
      >
        <Card className="group-hover:bg-muted/60 flex h-full flex-col justify-between transition-colors">
          <p data-kpi-label className="muted text-xs font-medium tracking-wide uppercase">
            {label}
          </p>
          <p
            className={cn(
              'font-heading mt-2 flex items-center gap-1 text-2xl font-semibold tabular-nums sm:text-3xl',
              toneClass,
            )}
          >
            {value}
            {/* The affordance that says this is a link, which a number on a
                card otherwise never does. */}
            <ChevronRight
              className="text-muted-foreground size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              aria-hidden
            />
          </p>
          {comparison && <Comparison current={current} unit={unit} comparison={comparison} />}
          {hint && <p className="muted mt-1 text-xs">{hint}</p>}
        </Card>
      </Link>

      {explain && (
        <Hint side="left" className="absolute top-4 right-4 sm:top-5 sm:right-5 pointer-fine:top-4 pointer-fine:right-4">
          {explain}
        </Hint>
      )}
    </div>
  )
}

function Comparison({
  current,
  unit,
  comparison,
}: {
  current: number | null | undefined
  unit: KpiUnit
  comparison: KpiComparison
}) {
  const t = useT()
  const delta = kpiDelta(current, comparison.previous, comparison.better)
  const previous =
    comparison.previous === null || comparison.previous === undefined
      ? '—'
      : formatValue(unit, comparison.previous, t)
  const against = t(comparison.against === 'start' ? 'kpi.atStart' : 'kpi.previous', {
    value: previous,
  })

  if (!delta) {
    return <p className="muted mt-1 text-xs">{against}</p>
  }

  const tone = {
    good: 'text-emerald-600 dark:text-emerald-400',
    bad: 'text-rose-600 dark:text-rose-400',
    neutral: 'text-[var(--text-strong)]',
  }[delta.tone]

  const arrow = delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '='
  const sign = delta.diff > 0 ? '+' : delta.diff < 0 ? '−' : ''

  return (
    <p className="mt-1 text-xs">
      <span className={cn('font-medium tabular-nums', tone)}>
        <span aria-hidden>{arrow}</span> {sign}
        {formatDiff(unit, Math.abs(delta.diff), t)}
      </span>
      <span className="sr-only">
        {' '}
        {t(
          delta.direction === 'up'
            ? 'kpi.direction.up'
            : delta.direction === 'down'
              ? 'kpi.direction.down'
              : 'kpi.direction.flat',
        )}
      </span>
      <span className="muted"> · {against}</span>
    </p>
  )
}

/** A previous figure, printed the way the tile prints its own. */
function formatValue(unit: KpiUnit, value: number, t: ReturnType<typeof useT>): string {
  return unit === 'points' ? formatPercent(value) : formatDiff(unit, value, t)
}

/**
 * A change, in the figure's own unit. A 5S average moving from 74% to 77%
 * moved three percentage points, not three percent — and "+3%" beside a
 * percentage invites exactly that misreading — so `points` says so.
 */
function formatDiff(unit: KpiUnit, value: number, t: ReturnType<typeof useT>): string {
  switch (unit) {
    case 'hours':
      return formatHours(value)
    case 'money':
      return formatMoney(value)
    case 'points':
      return t('kpi.points', { value: formatNumber(value) })
    default:
      return formatNumber(value)
  }
}
